import React, { useState, useMemo } from 'react';
import { Conversation, UserProfile } from '../types';
import {
  Pin,
  VolumeX,
  Check,
  CheckCheck,
  Users,
  Radio,
  Bot,
  Search,
  MessageSquarePlus,
  Sparkles,
  Lock,
  Crown,
  Gift,
  Archive,
  ArchiveRestore,
  MoreVertical,
  Trash2,
  Eraser,
  Ban,
  ShieldCheck,
  Plus,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';
import { ConfirmationModal, ConfirmationType } from './ConfirmationModal';

interface ConversationsListProps {
  conversations: Conversation[];
  activeConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  onToggleArchive?: (conversationId: string, archived: boolean) => void;
  currentUser: UserProfile;
  onlineUsers: Record<string, { isOnline: boolean; isTyping: boolean }>;
  onOpenPremium?: () => void;
  onOpenNewChat?: () => void;
  onDeleteConversation?: (convId: string) => void;
  onClearConversation?: (convId: string) => void;
  onBlockUser?: (targetUsername: string) => void;
  onUnblockUser?: (targetUsername: string) => void;
}

type FilterType = 'all' | 'unread' | 'groups' | 'channels' | 'ai' | 'archived';

export const ConversationsList: React.FC<ConversationsListProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onToggleArchive,
  currentUser,
  onlineUsers,
  onOpenPremium,
  onOpenNewChat,
  onDeleteConversation,
  onClearConversation,
  onBlockUser,
  onUnblockUser,
}) => {
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuConvId, setOpenMenuConvId] = useState<string | null>(null);
  const [activeConfirmation, setActiveConfirmation] = useState<{
    isOpen: boolean;
    type: ConfirmationType;
    title: string;
    description: string;
    confirmText?: string;
    isDestructive?: boolean;
    action: () => void;
  } | null>(null);

  const formatTimestamp = (ts?: number) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('ar-SA', { month: 'numeric', day: 'numeric' });
  };

  // Ensure absolutely unique conversation items by id
  const uniqueConversations = useMemo(() => {
    const seen = new Set<string>();
    return conversations.filter((c) => {
      if (!c || !c.id || seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [conversations]);

  const filteredConversations = useMemo(() => {
    return uniqueConversations
      .filter((c) => {
        const isArchived = c.archivedBy?.includes(currentUser.username);
        if (filter === 'archived') {
          if (!isArchived) return false;
        } else {
          if (isArchived) return false; // Hide archived from other views
        }

        // Type filter
        if (filter === 'unread' && (!c.unreadCount || c.unreadCount === 0)) return false;
        if (filter === 'groups' && c.type !== 'group') return false;
        if (filter === 'channels' && c.type !== 'channel') return false;
        if (filter === 'ai' && c.type !== 'ai') return false;

        // Search filter
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          return (
            c.title.toLowerCase().includes(q) ||
            c.lastMessage?.text?.toLowerCase().includes(q) ||
            c.description?.toLowerCase().includes(q)
          );
        }
        return true;
      })
      .sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        const timeA = a.lastMessage?.timestamp || a.createdAt;
        const timeB = b.lastMessage?.timestamp || b.createdAt;
        return timeB - timeA;
      });
  }, [uniqueConversations, filter, searchQuery, currentUser.username]);

  return (
    <div className="relative flex flex-col h-full bg-[#080d16] font-['Cairo'] border-l border-slate-800/80">
      {/* Search Bar */}
      <div className="p-3 pb-2 space-y-2">
        <div className="relative flex items-center">
          <Search className="w-4 h-4 absolute right-3 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="بحث في محادثات وتين..."
            className="w-full pl-3 pr-9 py-2 bg-[#121927] border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/60 transition"
          />
        </div>

        {/* Subtle Non-Intrusive Premium Trial Banner (if not premium) */}
        {!currentUser.isPremium && onOpenPremium && (
          <button
            type="button"
            onClick={onOpenPremium}
            className="w-full px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500/15 via-yellow-500/10 to-transparent border border-amber-500/30 hover:border-amber-400 text-right flex items-center justify-between transition group shadow-sm"
          >
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-amber-400 shrink-0 group-hover:scale-110 transition" />
              <div className="text-[11px] leading-tight">
                <span className="font-black text-amber-300">أول شهر مجاني في وتين Premium 🎁</span>
                <span className="block text-[10px] text-slate-400">شارة التاج 👑، ثيمات فاخرة، ورفع حتى 500MB</span>
              </div>
            </div>
            <span className="text-[10px] font-bold text-amber-400 group-hover:translate-x-[-2px] transition">
              تفعيل ←
            </span>
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="px-3 pb-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar border-b border-slate-800/60 text-xs">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition shrink-0 ${
            filter === 'all'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'text-slate-400 hover:text-white bg-[#121927]'
          }`}
        >
          الكل ({conversations.length})
        </button>

        <button
          onClick={() => setFilter('unread')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition shrink-0 ${
            filter === 'unread'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'text-slate-400 hover:text-white bg-[#121927]'
          }`}
        >
          غير مقروءة
        </button>

        <button
          onClick={() => setFilter('groups')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition shrink-0 ${
            filter === 'groups'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'text-slate-400 hover:text-white bg-[#121927]'
          }`}
        >
          المجموعات
        </button>

        <button
          onClick={() => setFilter('channels')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition shrink-0 ${
            filter === 'channels'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'text-slate-400 hover:text-white bg-[#121927]'
          }`}
        >
          القنوات
        </button>

        <button
          onClick={() => setFilter('ai')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition shrink-0 ${
            filter === 'ai'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'text-slate-400 hover:text-white bg-[#121927]'
          }`}
        >
          وتين AI 🤖
        </button>

        <button
          onClick={() => setFilter('archived')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition shrink-0 ${
            filter === 'archived'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'text-slate-400 hover:text-white bg-[#121927]'
          }`}
        >
          الأرشيف 📦
        </button>
      </div>

      {/* Conversation List Items */}
      <div className="flex-1 overflow-y-auto divide-y divide-slate-800/40">
        {filteredConversations.length === 0 ? (
          <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-48">
            <MessageSquarePlus className="w-10 h-10 text-slate-600 mb-2 stroke-[1.5]" />
            <p className="text-xs">لا توجد محادثات تطابق بحثك</p>
          </div>
        ) : (
          filteredConversations.map((conv, idx) => {
            const isActive = activeConversationId === conv.id;
            const otherUsername = conv.participants.find((p) => p !== currentUser.username);
            const isOnline = otherUsername ? onlineUsers[otherUsername]?.isOnline : false;
            const isTyping = otherUsername ? onlineUsers[otherUsername]?.isTyping : false;

            return (
              <div
                key={`${conv.id}_${idx}`}
                onClick={() => onSelectConversation(conv.id)}
                className={`w-full p-3 sm:px-4 text-right flex items-center gap-3 transition group cursor-pointer ${
                  isActive
                    ? 'bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-transparent border-r-4 border-emerald-400'
                    : 'hover:bg-[#121927]'
                }`}
              >
                {/* Avatar with Online or Type badge */}
                <div className="relative shrink-0">
                  <img
                    src={conv.avatar}
                    alt={conv.title}
                    className="w-12 h-12 rounded-2xl object-cover ring-1 ring-slate-800 group-hover:ring-emerald-500/40 transition"
                  />
                  {conv.type === 'ai' && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-lg bg-indigo-600 text-white flex items-center justify-center border-2 border-[#080d16] shadow-sm">
                      <Bot className="w-3 h-3" />
                    </div>
                  )}
                  {conv.type === 'channel' && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-lg bg-teal-600 text-white flex items-center justify-center border-2 border-[#080d16] shadow-sm">
                      <Radio className="w-3 h-3" />
                    </div>
                  )}
                  {conv.type === 'group' && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-lg bg-emerald-600 text-white flex items-center justify-center border-2 border-[#080d16] shadow-sm">
                      <Users className="w-3 h-3" />
                    </div>
                  )}
                  {conv.type === 'direct' && isOnline && (
                    <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#080d16]" />
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 truncate">
                      <span className={`text-xs font-bold truncate ${isActive ? 'text-emerald-300' : 'text-white'}`}>
                        {conv.title}
                      </span>
                      {conv.title.includes('👑') && (
                        <span className="text-[10px] text-amber-400 font-bold" title="مشترك في وتين Premium">
                          👑
                        </span>
                      )}
                      {conv.isPinned && <Pin className="w-3 h-3 text-emerald-400 rotate-45 shrink-0" />}
                    </div>
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {formatTimestamp(conv.lastMessage?.timestamp || conv.createdAt)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] text-slate-400 truncate flex items-center gap-1">
                      {isTyping ? (
                        <span className="text-emerald-400 font-bold animate-pulse">يكتب الآن...</span>
                      ) : conv.lastMessage ? (
                        <>
                          {conv.lastMessage.senderUsername === currentUser.username && (
                            <span className="text-slate-500">
                              {conv.lastMessage.status === 'read' && currentUser.privacy?.readReceipts !== false ? (
                                <CheckCheck className="w-3.5 h-3.5 text-sky-400 inline" />
                              ) : conv.lastMessage.status === 'delivered' || conv.lastMessage.status === 'read' ? (
                                <CheckCheck className="w-3.5 h-3.5 text-slate-500 inline" />
                              ) : (
                                <Check className="w-3.5 h-3.5 text-slate-500 inline" />
                              )}
                            </span>
                          )}
                          <span className="truncate">
                            {conv.lastMessage.isSecretReveal
                              ? '🔒 رسالة سرية مشفرة'
                              : conv.lastMessage.text || 'مرفق وسائط'}
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-500 italic">لا توجد رسائل بعد</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* More Menu Button */}
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuConvId(openMenuConvId === conv.id ? null : conv.id);
                          }}
                          className="p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                          title="خيارات إضافية"
                        >
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>

                        {openMenuConvId === conv.id && (
                          <div
                            className="absolute left-0 mt-1 w-48 bg-[#0e1422] border border-slate-800 rounded-2xl p-1.5 shadow-2xl z-50 text-right space-y-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {conv.type === 'direct' && otherUsername && otherUsername !== currentUser.username && (
                              <button
                                onClick={() => {
                                  setOpenMenuConvId(null);
                                  const isBlocked = currentUser.blockedUsers?.includes(otherUsername);
                                  if (isBlocked) {
                                    setActiveConfirmation({
                                      isOpen: true,
                                      type: 'unblock_user',
                                      title: 'إلغاء حظر المستخدم',
                                      description: `هل ترغب في إلغاء حظر @${otherUsername}؟`,
                                      confirmText: 'نعم، فك الحظر',
                                      isDestructive: false,
                                      action: () => onUnblockUser?.(otherUsername),
                                    });
                                  } else {
                                    setActiveConfirmation({
                                      isOpen: true,
                                      type: 'block_user',
                                      title: 'حظر المستخدم',
                                      description: `هل أنت متأكد من رغبتك في حظر @${otherUsername}؟ لن يتمكن من مراسلتك مجدداً.`,
                                      confirmText: 'نعم، احظر الشخص',
                                      isDestructive: true,
                                      action: () => onBlockUser?.(otherUsername),
                                    });
                                  }
                                }}
                                className="w-full p-2 rounded-xl flex items-center justify-between text-[11px] font-semibold text-rose-300 hover:bg-rose-500/15 transition"
                              >
                                <span>{currentUser.blockedUsers?.includes(otherUsername) ? 'فك الحظر' : 'حظر الشخص'}</span>
                                {currentUser.blockedUsers?.includes(otherUsername) ? (
                                  <ShieldCheck className="w-3.5 h-3.5 text-teal-400" />
                                ) : (
                                  <Ban className="w-3.5 h-3.5 text-rose-400" />
                                )}
                              </button>
                            )}

                            {onClearConversation && (
                              <button
                                onClick={() => {
                                  setOpenMenuConvId(null);
                                  setActiveConfirmation({
                                    isOpen: true,
                                    type: 'clear_chat',
                                    title: 'مسح سجل المحادثة',
                                    description: `هل أنت متأكد من رغبتك في مسح كافة الرسائل في هذه المحادثة؟`,
                                    confirmText: 'نعم، امسح الرسائل',
                                    isDestructive: true,
                                    action: () => onClearConversation(conv.id),
                                  });
                                }}
                                className="w-full p-2 rounded-xl flex items-center justify-between text-[11px] font-semibold text-amber-300 hover:bg-amber-500/15 transition"
                              >
                                <span>مسح الدردشة</span>
                                <Eraser className="w-3.5 h-3.5 text-amber-400" />
                              </button>
                            )}

                            {onDeleteConversation && (
                              <button
                                onClick={() => {
                                  setOpenMenuConvId(null);
                                  setActiveConfirmation({
                                    isOpen: true,
                                    type: 'delete_conversation',
                                    title: 'حذف المحادثة',
                                    description: `هل أنت متأكد من رغبتك في حذف هذه المحادثة نهائياً من قائمتك؟`,
                                    confirmText: 'نعم، احذف المحادثة',
                                    isDestructive: true,
                                    action: () => onDeleteConversation(conv.id),
                                  });
                                }}
                                className="w-full p-2 rounded-xl flex items-center justify-between text-[11px] font-semibold text-red-400 hover:bg-red-500/15 transition"
                              >
                                <span>حذف المحادثة</span>
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Archive Button */}
                      {onToggleArchive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const isArchived = conv.archivedBy?.includes(currentUser.username);
                            onToggleArchive(conv.id, !isArchived);
                          }}
                          className="p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition opacity-0 group-hover:opacity-100 focus:opacity-100"
                          title={conv.archivedBy?.includes(currentUser.username) ? "إلغاء الأرشيف" : "أرشفة المحادثة"}
                        >
                          {conv.archivedBy?.includes(currentUser.username) ? (
                            <ArchiveRestore className="w-3.5 h-3.5" />
                          ) : (
                            <Archive className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}

                      {/* Unread badge */}
                      {conv.unreadCount !== undefined && conv.unreadCount > 0 && (
                        <span className="min-w-5 h-5 px-1.5 rounded-full bg-emerald-500 text-[#080d16] text-[10px] font-black flex items-center justify-center shadow-md">
                          {conv.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Floating Action Button for New Chat / Group */}
      {onOpenNewChat && (
        <div className="absolute bottom-5 left-5 z-20">
          <button
            onClick={onOpenNewChat}
            className="relative group w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-600 via-teal-500 to-emerald-400 hover:from-emerald-500 hover:to-teal-400 text-white shadow-xl shadow-emerald-500/40 hover:shadow-emerald-500/70 border border-emerald-300/50 transition-all duration-300 active:scale-90 flex items-center justify-center overflow-hidden animate-living-breathe"
            title="إنشاء محادثة أو مجموعة جديدة (+)"
          >
            {/* Ambient halo backdrop */}
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-400 via-teal-300 to-indigo-400 rounded-2xl blur-md opacity-60 animate-pulse-halo -z-10" />
            {/* Sweeping shimmer beam */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer-sweep pointer-events-none" />
            {/* Animated slow-spinning Plus */}
            <Plus className="w-6 h-6 transition-transform duration-300 group-hover:rotate-90 group-hover:scale-125 drop-shadow-[0_0_8px_rgba(255,255,255,0.8)] animate-subtle-spin-slow" />
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {activeConfirmation && (
        <ConfirmationModal
          isOpen={activeConfirmation.isOpen}
          type={activeConfirmation.type}
          title={activeConfirmation.title}
          description={activeConfirmation.description}
          confirmText={activeConfirmation.confirmText}
          isDestructive={activeConfirmation.isDestructive}
          onConfirm={() => {
            activeConfirmation.action();
            setActiveConfirmation(null);
          }}
          onCancel={() => setActiveConfirmation(null)}
        />
      )}
    </div>
  );
};
